// Stocky の CSV／画面貼り付けからの移行。
//
// Low Stock の発注点・仕入先別リードタイムと棚卸し履歴は CSV、MOQ は
// Order limits の画面貼り付けから移す。仕入先そのものは Stocky に CSV 出力が
// 無いため、下の supplier 系 API は手作り CSV 用の非表示経路として残している。
//
// ファイルは base64 の JSON ボディで送る（multipart 依存を増やさない）。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
  type StockySourceArchiveId,
} from "./stocky_public_ids";

// base64 化は発注点CSV取り込みと同じ実装を共有する（2つ持たない）。
export { fileToBase64 } from "./reorder_policy_import";

/** 取り込めなかった行（行番号＋日本語の理由）。 */
export interface CsvInvalidRow {
  row: number;
  reason: string;
}

export interface CsvPreview {
  headers: string[];
  sample_rows: string[][];
  total_rows: number;
  /** ヘッダ名からの対応付けの推測（初期値の提案。確定はマーチャント）。 */
  guessed_mapping: Record<string, string | null>;
  /** サーバーが固有ヘッダー署名を確認した形式。未知・旧APIではnull/undefined。 */
  detected_format?: "stocky_stocktake" | null;
}

/** 列の対応付け。使わない任意列は null で送る。 */
export type CsvMapping = Record<string, string | null>;

/** 3系統に共通する応答の骨。件数の名前は系統ごとに増える。 */
export interface CsvApplyResultBase {
  total_rows: number;
  invalid_rows: CsvInvalidRow[];
  /** 🔴 読み飛ばした行の**真の件数**。invalid_rows は表示用に先頭 N 件へ切り詰めて
   * あるので `.length` を件数として画面に出さない（500行の失敗が「20件」に見える）。
   * 突合できなかったキーの真の件数も同様。旧 API では欠ける（0 扱い）。 */
  invalid_row_count?: number;
  unmatched_count?: number;
  /** true なら件数を数えただけで DB には書いていない（確定前の確認）。 */
  dry_run: boolean;
  /** dry-runが確認したexact input/tenant/DB plan。対応endpointの本適用で必須。 */
  plan_token?: string | null;
  /**
   * 運用データへの正規化状態。新しい bounded importer は5項目を必ず
   * 返すが、旧endpointの段階的移行中も同じパネルを使うため型上は optional。
   * `retention_only` は原本保全だけを行い、運用モデルには1件も書かない。
   */
  normalization_mode?: "domain" | "retention_only";
  normalized_rows?: number;
  unprocessed_rows?: number;
  domain_normalization_complete?: boolean;
  retention_reason?: string | null;
  /** New importers return the exact retained source occurrence after apply. */
  source_archive?: StockyImportArchiveReceipt | null;
}

export interface StockyImportArchiveReceipt {
  id: StockySourceArchiveId;
  source_import_id: string;
  already_archived: boolean;
  duplicate_content: boolean;
  duplicate_archive_ids: StockySourceArchiveId[];
  duplicate_count: number;
  duplicate_first_archive_id?: StockySourceArchiveId | null;
  duplicate_last_archive_id?: StockySourceArchiveId | null;
  warnings: string[];
}

export interface SupplierCsvResult extends CsvApplyResultBase {
  created: number;
  updated: number;
}

export interface SupplierLinkCsvResult extends CsvApplyResultBase {
  linked_variants: number;
  /** CSV にあって手元に無かった仕入先名は自動で作られる＝その件数。 */
  suppliers_created: number;
  conditions_written: number;
  unmatched_skus: string[];
}

export interface ReorderPolicyCsvResult extends CsvApplyResultBase {
  created: number;
  updated: number;
  unchanged: number;
  lead_times_created: number;
  lead_times_updated: number;
  lead_times_unchanged: number;
  preferred_repaired: number;
  matched_variants: number;
  unmatched_skus: string[];
  ambiguous_skus: string[];
  ambiguous_sku_count: number;
  unmatched_suppliers: string[];
  unmatched_supplier_count: number;
  ambiguous_suppliers: string[];
  ambiguous_supplier_count: number;
}

export interface StocktakeHistoryCsvResult extends CsvApplyResultBase {
  recorded_items: number;
  /** 数量が読み取れず記録しなかった行数（欠損記号・空欄）。 */
  skipped_rows: number;
  /** 自店の商品として見つからなかったキー（SKU または Shopify のバリアントID）。 */
  unmatched_keys: string[];
  /** 作成した棚卸しの ID。dry-run と「1件も記録できなかった」ときは null。 */
  stocktake_id: StockyPublicBigIntId | null;
  /** 同じ取込UUID・完全一致入力の通信再試行。2件目は作らない。 */
  already_imported: boolean;
  /** 無加工ファイル bytes の SHA-256（来歴照合用）。 */
  source_file_sha256: string;
  /** 最初に記録した元ファイル名（来歴照合用）。 */
  source_file_name: string;
  /** 1回のファイル選択からdry-run/apply/retryまで固定するUUID。 */
  source_import_id: string;
  /** 同じraw bytesを別取込UUIDで保存済みの履歴数（警告のみ）。 */
  same_source_file_imports: number;
  matched_current_items: number;
  source_only_items: number;
  source_incomplete_items: number;
  source_quantity_interpretation: "unknown" | "counted" | "expected" | null;
  /** 新backendが保存・署名したStocky一覧行。指定したのに欠ければ適用を続けない。 */
  stocky_stocktake_list?: StockyStocktakeListMetadata | null;
  /** dry-run と同じ入力・商品解決結果だけを実適用する署名。 */
  plan_token: string | null;
  /** dry-runはnull。本適用とexact retryは保存済み原本の確定receiptを返す。 */
  source_archive: StockyImportArchiveReceipt | null;
}

export interface StockyStocktakeListMetadata {
  schema_version: 1;
  kind: "stocky_stocktake_list_row";
  association_method: "single_file_single_row" | "merchant_selected";
  csv_contains_stocky_identifier: false;
  number_raw: string;
  name_raw: string;
  location_raw: string;
  generated_at: {
    raw: string;
    local_iso: string | null;
    timezone: null;
    meaning: "stocky_generated_at";
  };
  status_raw: string;
  submitted_header_text: string;
  submitted_row_text: string;
}

interface ApplyOptions {
  dryRun: boolean;
  expectedPlanToken?: string;
  confirmRetentionOnly?: boolean;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
}

function previewCsv(path: string, fileBase64: string): Promise<CsvPreview> {
  return apiFetch<CsvPreview>(path, {
    method: "POST",
    body: { file_base64: fileBase64 },
  });
}

export function normalizeStockyImportArchiveReceipt(
  value: unknown,
): StockyImportArchiveReceipt {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky import source archive receipt");
  }
  const receipt = value as StockyImportArchiveReceipt & {
    id: unknown;
    duplicate_archive_ids: unknown;
    duplicate_first_archive_id?: unknown;
    duplicate_last_archive_id?: unknown;
  };
  if (!Array.isArray(receipt.duplicate_archive_ids)) {
    throw new TypeError("invalid Stocky import source archive receipt");
  }
  if (
    typeof receipt.source_import_id !== "string" ||
    typeof receipt.already_archived !== "boolean" ||
    typeof receipt.duplicate_content !== "boolean" ||
    !Number.isSafeInteger(receipt.duplicate_count) ||
    receipt.duplicate_count < 0 ||
    !Array.isArray(receipt.warnings) ||
    !receipt.warnings.every((warning) => typeof warning === "string")
  ) {
    throw new TypeError("invalid Stocky import source archive receipt");
  }
  return {
    id: canonicalStockyPublicBigIntId(receipt.id),
    source_import_id: receipt.source_import_id,
    already_archived: receipt.already_archived,
    duplicate_content: receipt.duplicate_content,
    duplicate_archive_ids: receipt.duplicate_archive_ids.map(
      canonicalStockyPublicBigIntId,
    ),
    duplicate_count: receipt.duplicate_count,
    duplicate_first_archive_id:
      receipt.duplicate_first_archive_id == null
        ? null
        : canonicalStockyPublicBigIntId(receipt.duplicate_first_archive_id),
    duplicate_last_archive_id:
      receipt.duplicate_last_archive_id == null
        ? null
        : canonicalStockyPublicBigIntId(receipt.duplicate_last_archive_id),
    warnings: [...receipt.warnings],
  };
}

async function applyCsv<TResult extends CsvApplyResultBase>(
  path: string,
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
  /** 系統ごとの追加フィールド（棚卸し履歴の実施日など）。 */
  extra: Record<string, unknown> = {},
): Promise<TResult> {
  const response = await apiFetch<TResult>(path, {
    method: "POST",
    body: {
      file_base64: fileBase64,
      mapping,
      dry_run: options.dryRun,
      source_import_id: options.sourceImportId,
      source_file_name: options.sourceFileName,
      source_media_type: options.sourceMediaType,
      ...(options.expectedPlanToken === undefined
        ? {}
        : { expected_plan_token: options.expectedPlanToken }),
      ...(options.confirmRetentionOnly === undefined
        ? {}
        : { confirm_retention_only: options.confirmRetentionOnly }),
      ...extra,
    },
  });
  if (
    response.source_archive === undefined ||
    response.source_archive === null
  ) {
    return response;
  }
  return {
    ...response,
    source_archive: normalizeStockyImportArchiveReceipt(
      response.source_archive,
    ),
  };
}

const SUPPLIERS = "/api/migrate/csv/suppliers";
const SUPPLIER_LINKS = "/api/migrate/csv/supplier-links";
// 🔴 旧 URL は Lead Time を商品全体の ReorderPolicy へ書く backend にも存在する。
// 専用 URL にすることで、新 SPA × 旧 backend の配信窓は 404 で安全に止まる。
const LOW_STOCK = "/api/migrate/csv/low-stock";
const STOCKTAKES = "/api/migrate/csv/stocktakes";

export function previewSupplierCsv(fileBase64: string): Promise<CsvPreview> {
  return previewCsv(`${SUPPLIERS}/preview`, fileBase64);
}

export function applySupplierCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<SupplierCsvResult> {
  return applyCsv<SupplierCsvResult>(
    `${SUPPLIERS}/apply`,
    fileBase64,
    mapping,
    options,
  );
}

export function previewSupplierLinkCsv(
  fileBase64: string,
): Promise<CsvPreview> {
  return previewCsv(`${SUPPLIER_LINKS}/preview`, fileBase64);
}

export function applySupplierLinkCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<SupplierLinkCsvResult> {
  return applyCsv<SupplierLinkCsvResult>(
    `${SUPPLIER_LINKS}/apply`,
    fileBase64,
    mapping,
    options,
  );
}

export function previewReorderPolicyCsv(
  fileBase64: string,
): Promise<CsvPreview> {
  return previewCsv(`${LOW_STOCK}/preview`, fileBase64);
}

export function applyReorderPolicyCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<ReorderPolicyCsvResult> {
  return applyCsv<ReorderPolicyCsvResult>(
    `${LOW_STOCK}/apply`,
    fileBase64,
    mapping,
    options,
  );
}

export function previewStocktakeHistoryCsv(
  fileBase64: string,
): Promise<CsvPreview> {
  return previewCsv(`${STOCKTAKES}/preview`, fileBase64);
}

export interface StocktakeHistoryLocationOption {
  id: StockyPublicBigIntId;
  shopify_location_id: StockyPublicBigIntId;
  name: string;
  active: boolean;
}

/** Stocky の Stocktakes 一覧からコピーした1行。CSV本体とは別の来歴。 */
export interface StockyStocktakeListMetadataInput {
  stocky_number: string;
  name: string;
  location_name: string;
  generated_at_raw: string;
  status: string;
  submitted_header_text: string;
  submitted_row_text: string;
  association_method: "single_file_single_row" | "merchant_selected";
}

/** FreeのStocky移行専用。inactiveを含むtenant内拠点を履歴選択用に返す。 */
export async function listStocktakeHistoryLocationOptions(): Promise<
  StocktakeHistoryLocationOption[]
> {
  const rows = await apiFetch<StocktakeHistoryLocationOption[]>(
    `${STOCKTAKES}/location-options`,
  );
  return rows.map((row) => ({
    ...row,
    id: canonicalStockyPublicBigIntId(row.id),
    shopify_location_id: canonicalStockyPublicBigIntId(row.shopify_location_id),
  }));
}

/**
 * 棚卸しを1件の履歴として記録する。🔴 在庫は動かない。
 *
 * `countedOn`（実施日）と `locationId` は CSV/Stocky画面に無い場合がある。
 * 分かる場合だけ送り、不明は null のまま保持する。
 */
export async function applyStocktakeHistoryCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
  extra: {
    countedOn: string | null;
    locationId: StockyPublicBigIntId | null;
    note: string | null;
    sourceImportId: string;
    sourceFileName: string;
    sourceMediaType: string;
    sourceQuantityInterpretation: "unknown" | "counted" | "expected" | null;
    stockyListMetadata?: StockyStocktakeListMetadataInput | null;
  },
): Promise<StocktakeHistoryCsvResult> {
  const result = await applyCsv<StocktakeHistoryCsvResult>(
    `${STOCKTAKES}/apply`,
    fileBase64,
    mapping,
    options,
    {
      counted_on: extra.countedOn,
      location_id:
        extra.locationId == null
          ? null
          : canonicalStockyPublicBigIntId(extra.locationId),
      note: extra.note,
      source_import_id: extra.sourceImportId,
      source_file_name: extra.sourceFileName,
      source_media_type: extra.sourceMediaType,
      source_quantity_interpretation: extra.sourceQuantityInterpretation,
      ...(extra.stockyListMetadata == null
        ? {}
        : { stocky_list_metadata: extra.stockyListMetadata }),
      ...(options.expectedPlanToken === undefined
        ? {}
        : { expected_plan_token: options.expectedPlanToken }),
    },
  );
  return {
    ...result,
    stocktake_id:
      result.stocktake_id == null
        ? null
        : canonicalStockyPublicBigIntId(result.stocktake_id),
  };
}

/** Stocky の Order limits 画面を貼り付けて MOQ を取り込む（CSV ではなく生テキスト）。 */
export interface MoqPasteRow {
  /** dry-run確認後はShopify variant IDが入り、表示と実適用対象を束縛する。 */
  shopify_variant_id: string | null;
  sku: string;
  /** null＝SKU は読めたが数量が見つからなかった（0 に倒さない）。 */
  quantity: number | null;
}

export interface MoqPastePreview {
  /** preview と apply を同じ仕入先へ束縛するため、サーバーが所有確認した値を返す。 */
  supplier_id: string;
  supplier_name: string;
  supplier_label: string;
  rows: MoqPasteRow[];
  /** 100万行の安全境界に使う、空行を除く物理行の真の件数。 */
  source_rows: number;
  /** 解析した SKU 行の真の件数。rows は表示用に先頭 preview_limit 件だけ。 */
  total_rows: number;
  matched_count: number;
  skipped_without_quantity_count: number;
  invalid_row_count: number;
  out_of_range_count: number;
  /** 同じ正規化 SKU を持つ商品が複数あり、対象を一意に決められない。 */
  ambiguous_skus: string[];
  ambiguous_sku_count: number;
  ambiguous_skus_truncated: boolean;
  /** 貼り付け内の同一 SKU に異なる数量がある。 */
  conflicting_skus: string[];
  conflicting_sku_count: number;
  conflicting_skus_truncated: boolean;
  /** 自店の商品として見つからなかった SKU らしき文字列。 */
  unknown_skus: string[];
  unknown_sku_count: number;
  /**
   * どの SKU にも結び付けられなかった数量セル。
   * 🔴 これを出さないと「2件貼ったのに1件しか読まれない」の理由が画面から消える
   * （Stocky 側で SKU 未設定の商品があると実際に起きる）。
   */
  orphan_quantities: string[];
  orphan_quantity_count: number;
  preview_limit: number;
  rows_truncated: boolean;
  unknown_skus_truncated: boolean;
  orphan_quantities_truncated: boolean;
  normalization_mode: "domain" | "retention_only";
  normalized_rows: number;
  unprocessed_rows: number;
  domain_normalization_complete: boolean;
  retention_reason: "hard_row_cap_exceeded" | null;
}

export interface MoqPasteResult extends CsvApplyResultBase {
  /** apply結果も、tokenが束縛したexact supplierをcanonical textで返す。 */
  supplier_id: string;
  supplier_name: string;
  supplier_label: string;
  /** この専用endpointでは常に真の件数を返す（旧CSV endpointとの互換optionalではない）。 */
  invalid_row_count: number;
  unmatched_count: number;
  created: number;
  updated: number;
  matched_variants: number;
  unmatched_skus: string[];
  /** 数量が読めず書き込まなかった SKU。 */
  skipped_without_quantity: string[];
  skipped_without_quantity_count: number;
  unknown_skus: string[];
  unknown_sku_count: number;
  /** どの SKU にも結び付けられなかった数量セル。 */
  orphan_quantities: string[];
  orphan_quantity_count: number;
  diagnostics_truncated: boolean;
  /** dry-run だけ非 null。実適用はこの署名・期限付き確認 token を必須にする。 */
  plan_token: string | null;
  /** plan_token と同じ Shop lock 内で確定した表示用preview。 */
  confirmed_preview: MoqPastePreview | null;
  source_rows: number;
  normalization_mode: "domain" | "retention_only";
  normalized_rows: number;
  unprocessed_rows: number;
  domain_normalization_complete: boolean;
  retention_reason: "hard_row_cap_exceeded" | null;
}

export interface MoqSupplierOption {
  id: string;
  name: string;
  /** 同名でも識別できる ID・会社/連絡先・hidden 状態込みの表示名。 */
  label: string;
  /** Operational visibility stays boolean for mixed-version compatibility. */
  is_hidden: boolean;
  /** false means Stocky omitted visibility; `is_hidden` is then only a sentinel. */
  is_hidden_known: boolean;
}

// 🔴 旧 URL は supplier_id を持たず商品全体の ReorderPolicy へ書いていた。
// 別 URL にすることで、新 SPA × 旧 backend の配信窓は 404 で安全に止まる。
const MOQ_PASTE = "/api/migrate/csv/supplier-moq-paste";

function normalizeMoqPasteRow(value: unknown): MoqPasteRow {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid MOQ paste row");
  }
  const row = value as Omit<MoqPasteRow, "shopify_variant_id"> & {
    shopify_variant_id: unknown;
  };
  return {
    ...row,
    shopify_variant_id:
      row.shopify_variant_id === null
        ? null
        : canonicalStockyPublicBigIntId(row.shopify_variant_id),
  };
}

function normalizeMoqPastePreview(value: unknown): MoqPastePreview {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid MOQ paste preview");
  }
  const preview = value as Omit<MoqPastePreview, "supplier_id" | "rows"> & {
    supplier_id: unknown;
    rows: unknown;
  };
  if (!Array.isArray(preview.rows)) {
    throw new TypeError("invalid MOQ paste preview");
  }
  return {
    ...preview,
    supplier_id: canonicalStockyPublicBigIntId(preview.supplier_id),
    rows: preview.rows.map(normalizeMoqPasteRow),
  };
}

function normalizeMoqPasteResult(value: unknown): MoqPasteResult {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid MOQ paste result");
  }
  const result = value as Omit<
    MoqPasteResult,
    "supplier_id" | "confirmed_preview"
  > & {
    supplier_id: unknown;
    confirmed_preview: unknown;
  };
  return {
    ...result,
    supplier_id: canonicalStockyPublicBigIntId(result.supplier_id),
    confirmed_preview:
      result.confirmed_preview === null
        ? null
        : normalizeMoqPastePreview(result.confirmed_preview),
  };
}

function normalizeMoqSupplierOption(value: unknown): MoqSupplierOption {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid MOQ supplier option");
  }
  const supplier = value as Omit<MoqSupplierOption, "id"> & { id: unknown };
  return {
    ...supplier,
    id: canonicalStockyPublicBigIntId(supplier.id),
  };
}

export async function listMoqSupplierOptions(): Promise<MoqSupplierOption[]> {
  // 新 SPA × options 未実装の旧 backend は404で止まり、商品数gate付きPO APIへ
  // fallbackしない。Free移行を塞がず、同時に未確認のapplyへ進ませない。
  const suppliers = await apiFetch<unknown>(`${MOQ_PASTE}/supplier-options`);
  if (!Array.isArray(suppliers)) {
    throw new TypeError("invalid MOQ supplier options");
  }
  return suppliers.map(normalizeMoqSupplierOption);
}

export async function previewMoqPaste(
  supplierId: StockyPublicBigIntId,
  text: string,
): Promise<MoqPastePreview> {
  const canonicalSupplierId = canonicalStockyPublicBigIntId(supplierId);
  const result = await apiFetch<unknown>(`${MOQ_PASTE}/preview`, {
    method: "POST",
    body: {
      supplier_id: canonicalSupplierId,
      text,
    },
  });
  const preview = normalizeMoqPastePreview(result);
  if (preview.supplier_id !== canonicalSupplierId) {
    throw new TypeError("MOQ preview supplier binding changed");
  }
  return preview;
}

export async function applyMoqPaste(
  supplierId: StockyPublicBigIntId,
  text: string,
  options: {
    dryRun: boolean;
    expectedPlanToken?: string;
    confirmRetentionOnly?: boolean;
    sourceImportId: string;
    sourceFileName: string;
    sourceMediaType: string;
  },
): Promise<MoqPasteResult> {
  const canonicalSupplierId = canonicalStockyPublicBigIntId(supplierId);
  const result = await apiFetch<unknown>(`${MOQ_PASTE}/apply`, {
    method: "POST",
    body: {
      supplier_id: canonicalSupplierId,
      text,
      dry_run: options.dryRun,
      source_import_id: options.sourceImportId,
      source_file_name: options.sourceFileName,
      source_media_type: options.sourceMediaType,
      ...(options.expectedPlanToken === undefined
        ? {}
        : { expected_plan_token: options.expectedPlanToken }),
      ...(options.confirmRetentionOnly === undefined
        ? {}
        : { confirm_retention_only: options.confirmRetentionOnly }),
    },
  });
  const applied = normalizeMoqPasteResult(result);
  if (
    applied.supplier_id !== canonicalSupplierId ||
    (applied.confirmed_preview !== null &&
      applied.confirmed_preview.supplier_id !== canonicalSupplierId)
  ) {
    throw new TypeError("MOQ apply supplier binding changed");
  }
  return applied;
}
